import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';

import { Athlete } from '@core/models/brijjsports.models';
import { ToastService } from '@core/services/toast.service';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

@Component({
  selector: 'app-basketball-scoring-setup',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatStepperModule
  ],
  templateUrl: './basketball-scoring-setup.component.html',
  styleUrl: './basketball-scoring-setup.component.scss'
})
export class BasketballScoringSetupComponent {
  private readonly state = inject(BrijjSportsStateService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  protected readonly basicsForm = this.fb.nonNullable.group({
    name: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(80)]),
    opponent: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(60)]),
    quarterCount: this.fb.nonNullable.control<2 | 4>(4)
  });

  protected readonly rosterControl = new FormControl<string[]>([], { nonNullable: true });

  protected readonly basketballAthletes = computed<Athlete[]>(() =>
    this.state.athletes().filter(a => a.sportKeys.includes('basketball') && !a.archived)
  );

  protected readonly canFinish = computed(() => this.basicsForm.valid && (this.rosterControl.value?.length ?? 0) > 0);

  protected readonly creating = signal(false);

  toggleAthlete(id: string, checked: boolean): void {
    const cur = new Set(this.rosterControl.value);
    if (checked) cur.add(id);
    else cur.delete(id);
    this.rosterControl.setValue(Array.from(cur));
  }

  isSelected(id: string): boolean {
    return this.rosterControl.value.includes(id);
  }

  async create(): Promise<void> {
    if (!this.canFinish() || this.creating()) return;
    this.creating.set(true);
    const v = this.basicsForm.getRawValue();
    const session = this.state.createBasketballGame({
      name: v.name.trim(),
      opponent: v.opponent.trim(),
      quarterCount: v.quarterCount,
      participantIds: this.rosterControl.value
    });
    this.state.startSession(session.id);
    this.toast.success('Game created', `${v.name} vs ${v.opponent}`);
    await this.router.navigate(['/coach/scoring/live', session.id]);
  }
}
